/**
 * B.11 — Operator «Отчёты» list. Three buckets: Нужен отчёт (red) → На
 * исправление (cyan, ИСПРАВИТЬ) → Сданные (GameReportCard). Tap any element →
 * GameReportModal in the right mode (new / fix / edit). Ported from screen-11.
 */
import { useEffect, useState } from "react";
import { api } from "../../api/client";
import { useOperatorReports, type OpUnreported, type OpReport } from "../../hooks/useOperatorReports";
import { ReportMedia } from "./ReportMedia";
import { ReportUnifans, UnifansToggle, useReportUnifans } from "./ReportUnifans";
import u from "./unifansBlock.module.css";
import { GameReportModal } from "./GameReportModal";
import { type ModalGame, rub } from "./reportModel";
import { WarnTri, Check } from "./icons";
import { PlayersBadge } from "../../components/shared"; /* 05.09 (владелец): стандартный бейдж игроков */
import { isMirKvSource } from "../../utils/mirkv";
import css from "./reports.module.css";

const ddmm = (iso: string): string => { if (!iso) return ""; const [, m, d] = iso.split("-"); return `${d}.${m}`; };

export function ReportsTab({ date, operatorId, operatorName }: { date: Date; operatorId?: string; operatorName: string }) {
  const { need, fix, done, loading, error, reload } = useOperatorReports(date, operatorId);
  const [modal, setModal] = useState<ModalGame | null>(null);
  /* 05.09 (владелец): «окно правок» — пока открыто, любой сданный отчёт можно изменить (mode fix без причины OO) */
  const [editUntil, setEditUntil] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    const load = () => api<{ open_until: string | null }>("/api/game-reports/edit-window").then((r) => { if (alive) setEditUntil(r.open_until ?? null); }).catch(() => {});
    load(); const t = window.setInterval(load, 60000);
    return () => { alive = false; window.clearInterval(t); };
  }, []);
  const windowOpen = !!editUntil && Date.parse(editUntil) > Date.now();
  const untilHm = editUntil ? (() => { const d = new Date(Date.parse(editUntil) + 5 * 3600000); return `${String(d.getUTCHours()).padStart(2, "0")}:${String(d.getUTCMinutes()).padStart(2, "0")}`; })() : "";

  function openNew(a: OpUnreported) {
    setModal({
      bookingId: a.bookingId, questName: a.questName, shortName: a.shortName, time: a.time,
      date: fmtIso(date), clientName: a.clientName, bookedPlayers: a.players, source: a.source,
      tea: a.tea, teaRoomId: a.teaRoomId, mc: a.mc, branchId: a.branchId,
      operatorId: operatorId ?? "", operatorName, mode: "new",
    });
  }
  function openReport(r: OpReport, mode: "fix" | "edit") {
    setModal({
      bookingId: r.bookingId, questName: r.questName, shortName: r.shortName, time: r.time,
      date: r.date || fmtIso(date), clientName: r.clientName, bookedPlayers: r.players, source: r.source,
      tea: r.tea, teaRoomId: r.teaRoomId, mc: r.mc, branchId: r.branchId,
      operatorId: operatorId ?? "", operatorName, mode, reportId: r.id,
      correctionReason: mode === "fix" ? r.correctionReason : undefined,
      prefill: { players: r.players, cash: r.cash, terminal: r.terminal, transfer: r.transfer, prepaidUsed: r.prepaidUsed, invoice: r.invoice, activity: r.activity, comment: r.comment },
    });
  }

  return (
    <div className={css.main}>
      {windowOpen ? <div className={css.winBanner}>🔓 До <b>{untilHm}</b> можно править любые свои сданные отчёты — кнопка «Изменить» под отчётом</div> : null}
      {error ? <div className={css.empty}>Ошибка: {error}</div> : null}

      {need.length > 0 ? (
        <>
          <div className={`${css.secHead} ${css.danger}`}>
            <span className={css.ico}><WarnTri /></span>Нужен отчёт <span className={css.cnt}>({need.length})</span>
          </div>
          {need.map((a) => (
            <div key={`need-${a.bookingId}`} className={`${css.needRow} ${isMirKvSource(a.source) ? css.mirkvSrc : ""}`} onClick={() => openNew(a)}>
              <span className={css.when}><span className={css.d}>{ddmm(fmtIso(date))}</span>{a.time}</span>
              <span className={css.quest}>{a.shortName}</span>
              <PlayersBadge count={a.players} bookingId={a.bookingId} size="sm" fallbackName={a.clientName} />
              <span className={css.cta}>Сдать ›</span>
            </div>
          ))}
        </>
      ) : null}

      {fix.length > 0 ? (
        <>
          <div className={`${css.secHead} ${css.fix}`}>
            <span className={css.ico}><WarnTri /></span>На исправление <span className={css.cnt}>({fix.length})</span>
          </div>
          {fix.map((r) => (
            <div key={`fix-${r.id}`} className={`${css.fixCard} ${isMirKvSource(r.source) ? css.mirkvSrc : ""}`}>
              <div className={css.fixHead}>
                <span className={css.fixWhen}><span className={css.d}>{ddmm(r.date)}</span>{r.time}</span>
                <span className={css.fixQuest}>{r.shortName}</span>
              </div>
              <div className={css.fixReason}><b>OO:</b> {r.correctionReason}</div>
              <button type="button" className={css.btnFix} onClick={() => openReport(r, "fix")}>Исправить</button>
            </div>
          ))}
        </>
      ) : null}

      <div className={`${css.secHead} ${css.doneHead}`}>
        <span className={css.ico}><Check /></span>Сданные <span className={css.cnt}>({done.length})</span>
      </div>

      {loading ? (
        <div className={css.empty}>Загрузка…</div>
      ) : done.length === 0 && need.length === 0 ? (
        <div className={css.empty}>Нет игр на этот день или расписание не опубликовано</div>
      ) : (
        /* A45: сданный отчёт не редактируется — правки только через возврат OO (бакет «На исправление»);
           05.09: исключение — открытое ОО «окно правок»; медиа и (04.11) Юнифанс — правятся всегда */
        done.map((r) => (
          <div key={`rep-${r.id}`}>
            <GameReportCard r={r} />
            {windowOpen ? <button type="button" className={`${css.btnFix} ${css.btnEditWin}`} onClick={() => openReport(r, "fix")}>Изменить (до {untilHm})</button> : null}
          </div>
        ))
      )}

      {modal ? (
        <GameReportModal game={modal} onClose={() => setModal(null)} onSuccess={() => { setModal(null); reload(); }} />
      ) : null}
    </div>
  );
}

function GameReportCard({ r, onClick }: { r: OpReport; onClick?: () => void }) {
  /* 11.09 (оператор через владельца): у напарника денег в отчёте нет по замыслу — их сдаёт
     основной. Считать ему отклонение от ожидания нельзя: получалась вся выручка игры
     красной недостачей, и отчёт читался как заполненный неправильно. */
  const partner = !r.isPrimary;
  const diff = partner ? 0 : r.total - r.expectedTotal;
  // Медиа и Юнифанс (теги, ачивки; 04.11) правятся в сданном отчёте — исключение из A45.
  const [mediaOpen, setMediaOpen] = useState(false);
  const [ufOpen, setUfOpen] = useState(false);
  const uf = useReportUnifans(r.bookingId);
  return (
    <article className={`${css.repCard} ${isMirKvSource(r.source) ? css.mirkvSrc : ""}`} onClick={onClick}>
      <div className={css.repHead}>
        <span className={css.repWhen}><span className={css.d}>{ddmm(r.date)}</span>{r.time}</span>
        <span className={css.repQuest}>{r.shortName}</span>
        {r.isPrimary ? <span className={css.repStar}>★</span> : null}
        <span className={css.repSum} title={partner ? "Деньги по игре сдаёт основной оператор" : undefined}>
          {partner ? "напарник" : rub(r.total)}
        </span>
      </div>
      <div className={css.repBreak}>
        {r.cash > 0 ? <span className={css.pay}><span className={css.lbl}>нал</span><span className={css.v}>{rub(r.cash)}</span></span> : null}
        {r.terminal > 0 ? <span className={css.pay}><span className={css.lbl}>терм</span><span className={css.v}>{rub(r.terminal)}</span></span> : null}
        {r.transfer > 0 ? <span className={css.pay}><span className={css.lbl}>перев</span><span className={css.v}>{rub(r.transfer)}</span></span> : null}
        {r.prepaidUsed > 0 ? <span className={css.pay}><span className={css.lbl}>предопл</span><span className={css.v}>{rub(r.prepaidUsed)}</span></span> : null}
        <PlayersBadge count={r.players} bookingId={r.bookingId} size="sm" fallbackName={r.clientName} />
      </div>
      {diff !== 0 ? (
        <div className={`${css.repDiff} ${diff > 0 ? css.up : css.down}`}>
          {diff > 0 ? "▲ " : "▼ "}{rub(Math.abs(diff))} от ожидания
        </div>
      ) : null}
      {r.activity ? <div className={css.repLine}>Активность: <b>{r.activity}</b></div> : null}
      {r.comment ? <div className={`${css.repLine} ${css.note}`}>{r.comment}</div> : null}
      {r.ooComment ? <div className={css.repOo}><b>OO:</b> {r.ooComment}</div> : null}
      <div className={u.btns}>
        <UnifansToggle uf={uf} open={ufOpen} onToggle={() => setUfOpen((v) => !v)} />
        <button type="button" className={css.mediaToggle}
          onClick={(e) => { e.stopPropagation(); setMediaOpen((v) => !v); }}>
          {mediaOpen ? "Скрыть медиа" : "Медиа игры"}
        </button>
      </div>
      {ufOpen ? <ReportUnifans bookingId={r.bookingId} uf={uf} /> : null}
      {mediaOpen ? (
        <div onClick={(e) => e.stopPropagation()}>
          <ReportMedia bookingId={r.bookingId} />
        </div>
      ) : null}
    </article>
  );
}

function fmtIso(d: Date): string {
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
